import {
    Button,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    FilterBar,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    StatusBadge,
    Text,
} from "@athena/ui";
import { useCallback, useMemo, useState } from "react";
import { useApolloClient, useMutation, useQuery } from "@apollo/client/react";
import { toast } from "sonner";
import {
    OrganizationMemberCountFilteredDocument,
    OrganizationMembersDocument,
    OrganizationMembersNotAgeRestrictedDocument,
    OrganizationRosterCountDocument,
    OrganizationRosterDocument,
    SetMembershipLicensedDocument,
    SetOrganizationMembersAgeRestrictedDocument,
    type OrganizationMembersQuery,
    type OrganizationRosterQuery,
} from "../../graphql/organizations.generated";
import { UpdateUserDocument } from "../../graphql/users.generated";
import { formatDateTime } from "../../../../global/formatHelpers";
import { AddOrgMemberDialog } from "./AddOrgMemberDialog";
import { UserTable, userColumns } from "@/components/tables/UserTable";
import { ChevronDown, Download, ShieldOff, Users } from "lucide-react";
import { exportCsv } from "@/lib/exportCsv";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { DEFAULT_PAGE_SIZE, useWindowedRows } from "@/hooks/useWindowedRows";
import { useListView } from "@/hooks/useListView";
import { ListViewControls } from "@/components/tables/ListViewControls";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { ConfirmDialog } from "@/components/ConfirmDialog";

type Member = OrganizationMembersQuery["organizationMembers"][number];

const CSV_HEADERS = [
    "First Name",
    "Last Name",
    "Email",
    "Phone Number",
    "Licensed",
    "Status",
    "Last Active",
    "Joined",
];

/** One request per page while exporting, and a ceiling so a bad filter cannot loop forever. */
const EXPORT_PAGE_SIZE = 200;
const EXPORT_MAX_ROWS = 50_000;

const memberToCsvRow = (member: Member): string[] => {
    const user = member.user;
    return [
        user.firstName ?? "",
        user.lastName ?? "",
        user.email,
        user.phoneNumber ?? "",
        member.licensed ? "Yes" : "No",
        user.status === "ACTIVE" ? "Active" : "Inactive",
        user.lastActiveAt ? formatDateTime(user.lastActiveAt) : "",
        formatDateTime(member.createdAt),
    ];
};

/** No account yet, so there's nothing to report for the columns a real member has — bar the
 * phone number, which the enrollment itself carries. */
const pendingToCsvRow = (invitee: PendingMember): string[] => [
    invitee.firstName ?? "",
    invitee.lastName ?? "",
    invitee.email,
    invitee.phoneNumber ?? "",
    "",
    "Invited",
    "",
    "",
];

/** Sentinel for "no filter" — Radix Select has no unset value, only a chosen one. */
const ALL = "__all__";

/** Auto-enrolled, no account yet — they claim one by signing in. */
export interface PendingMember {
    email: string;
    firstName?: string | null;
    lastName?: string | null;
    /** From the enrollment record — an invitation has no account to carry one. */
    phoneNumber?: string | null;
}

interface OrganizationUsersProps {
    organizationId: string;
    /**
     * How many members the organization has, counted in the database.
     *
     * Passed in rather than derived from the rows below, which are one page of them. Used only to
     * tell an empty organization from one whose first page is still in flight.
     */
    memberCount: number;
    onMembersAdded: () => void;
    /**
     * When set, only members of this organization are eligible to be added — used when this org is
     * a sub-org, where an eligible user must already belong to its parent. Omit for a top-level
     * org, where any user is eligible.
     *
     * An organization id rather than a list of user ids: the picker filters on the server, so the
     * eligible set is never read into the browser to be intersected here.
     */
    restrictToOrganizationId?: string;
    /**
     * Invited people, listed alongside the real accounts.
     *
     * Without them this tab looked broken after an auto-enroll: the upload said it
     * enrolled four people and the Users tab didn't budge, because an invitation has
     * no `User` row until its first sign-in.
     */
    /**
     * How many invitations there are, counted by the database.
     *
     * The rows themselves are this tab's own paged read — see `invitations` below. This is the
     * unfiltered total, which is what the header beside the search box reports.
     */
    pendingCount?: number;
    /** Controls the add-member dialog — opened from a page-header button. */
    addMemberOpen: boolean;
    onAddMemberOpenChange: (open: boolean) => void;
}

export function OrganizationUsers({
    organizationId,
    memberCount,
    onMembersAdded,
    restrictToOrganizationId,
    pendingCount = 0,
    addMemberOpen,
    onAddMemberOpenChange,
}: OrganizationUsersProps) {
    const client = useApolloClient();
    const [
        search,
        setSearch,
    ] = useState<string>("");
    const [
        licenseFilter,
        setLicenseFilter,
    ] = useState<string>(ALL);

    /*
     * Searched and filtered in the database, not in the browser.
     *
     * This tab used to receive the whole roster as a prop — a field on the organization, fetched
     * with it — and filter the array. On the largest organization that is 8,635 memberships each
     * carrying a full `User`, read on every tab of the page, and then rendered as 8,635 table rows.
     *
     * Once the roster is paged, a browser-side filter is not merely slower but wrong: it would
     * search the fifty rows that happen to be loaded and report "no members match" for someone on
     * page nine. So both the search term and the license filter go to the server.
     */
    const settledSearch = useDebouncedValue(search);
    /*
     * Debounced while typing, immediate when emptied — clearing is a discrete action, not a
     * keystroke. Same reasoning as the platform directory, which had the delay leave the narrowed
     * request in flight for a third of a second after the box was already empty.
     */
    const queryTerm = search.trim() === "" ? "" : settledSearch.trim();
    /*
     * Keys are present only when set, so an unfiltered roster sends `{ organizationId }` and
     * nothing else. Sending `search: undefined` would put the key in the request, which the cache
     * policy and every test mock then have to match on.
     *
     * Memoized because both consumers key on it: `useWindowedRows` resets its exhausted flag when
     * the serialized variables change, and `useQuery` re-runs on a new object identity.
     */
    const filter = useMemo(
        () => ({
            organizationId,
            ...(queryTerm ? { search: queryTerm } : {}),
            ...(licenseFilter !== ALL ? { licensed: licenseFilter === "LICENSED" } : {}),
        }),
        [
            organizationId,
            queryTerm,
            licenseFilter,
        ],
    );

    const {
        rows: members,
        loading,
        loadingMore,
        hasMore,
        loadMore,
    } = useWindowedRows<Member>(
        OrganizationMembersDocument,
        (d) => (d as OrganizationMembersQuery | undefined)?.organizationMembers,
        { variables: filter },
    );

    const { data: countData } = useQuery(OrganizationMemberCountFilteredDocument, { variables: filter });
    const matchingCount = countData?.organizationMemberCount;

    /*
     * Invitations are a paged read of their own now, not a prop.
     *
     * They arrived as `pending`, one un-paged `organizationRoster` call capped at the repository's
     * safety limit. On an organization whose roster is mostly invitations that is the whole tab:
     * Demo University reported 8,500 invited in the header and listed 1,000 of them, with nothing
     * to click for the rest.
     */

    /*
     * Invitations are hidden entirely while a license filter is on.
     *
     * A license belongs to an account, and an invitation has no account yet — so an invitee is
     * neither licensed nor unlicensed, and showing them under either filter would be answering a
     * question they cannot be asked. Hiding them beats inventing a value: the alternative is a
     * "Licensed" list that includes people who hold no license.
     */
    const showInvitations = licenseFilter === ALL;
    const inviteVariables = useMemo(
        () => ({ organizationId, status: "PENDING" as const, ...(queryTerm ? { search: queryTerm } : {}) }),
        [
            organizationId,
            queryTerm,
        ],
    );
    const {
        rows: invitations,
        loadingMore: loadingMoreInvitations,
        hasMore: hasMoreInvitations,
        loadMore: loadMoreInvitations,
    } = useWindowedRows<PendingMember>(
        OrganizationRosterDocument,
        (d) => (d as OrganizationRosterQuery | undefined)?.organizationRoster,
        { variables: inviteVariables, skip: !showInvitations },
    );

    const { data: inviteCountData } = useQuery(OrganizationRosterCountDocument, {
        variables: inviteVariables,
        skip: !showInvitations,
    });
    const matchingInvitations = showInvitations ? (inviteCountData?.organizationRosterCount ?? 0) : 0;

    /*
     * One stream: every member, then every invitation.
     *
     * Two independently paged lists shown as one, so the reader reaches the end of the members
     * before the invitations begin. `loadMore` asks whichever half still has rows, which is what
     * lets a scroll or a Next button run past the join between them.
     */
    const visiblePending = showInvitations ? invitations : [];
    const combined = useMemo(
        () => [
            ...members.map((member) => ({ kind: "member" as const, member })),
            ...visiblePending.map((invitee) => ({ kind: "invitee" as const, invitee })),
        ],
        [
            members,
            visiblePending,
        ],
    );
    const anyMore = hasMore || (showInvitations && hasMoreInvitations);
    const loadMoreCombined = useCallback(() => {
        if (hasMore) loadMore();
        else if (showInvitations && hasMoreInvitations) loadMoreInvitations();
    }, [
        hasMore,
        loadMore,
        showInvitations,
        hasMoreInvitations,
        loadMoreInvitations,
    ]);

    /* Scroll or pages, the reader's choice, portal-wide — see `useListView`. */
    const view = useListView(combined, { hasMore: anyMore, loadMore: loadMoreCombined, pageSize: DEFAULT_PAGE_SIZE });

    const sentinelRef = useInfiniteScroll(loadMoreCombined, anyMore);

    const term = queryTerm.toLowerCase();
    const filtersActive = term !== "" || licenseFilter !== ALL;

    /**
     * Members and invitations in one list, mapped onto the shared column shape.
     *
     * An invitation has no account yet, so it has no license, last-login or joined date — every one of
     * those columns renders a dash for it, which is the honest answer. Its status is "Invited", which
     * is a real answer rather than a dash, so it goes in the status cell. The phone number is the
     * exception: the enrollment carries one, so it is shown rather than dashed.
     */
    const rows = view.rows.map((entry) =>
        entry.kind === "member"
            ? {
                  ...entry.member.user,
                  licensed: entry.member.licensed,
                  joinedAt: entry.member.createdAt,
              }
            : {
                  // No user row exists, so the email is the only stable identity — and it is what
                  // seeds the avatar color, so an invitation keeps its color once it becomes an
                  // account.
                  id: entry.invitee.email,
                  firstName: entry.invitee.firstName,
                  lastName: entry.invitee.lastName,
                  email: entry.invitee.email,
                  // The enrollment record's own number. An invitation has no account to read one
                  // from, which is why this column was blank for everybody an upload had just
                  // added — the number was in the database the whole time.
                  phoneNumber: entry.invitee.phoneNumber,
                  // In the Status column, where it reads as this row's status rather than as a note
                  // under the name — and where it keeps every row the same height.
                  statusNode: (
                      <StatusBadge dot variant="muted">
                          Invited
                      </StatusBadge>
                  ),
              },
    );

    const [setMembershipLicensed] = useMutation(SetMembershipLicensedDocument);
    const [
        pendingUserId,
        setPendingUserId,
    ] = useState<string | null>(null);
    const [
        exporting,
        setExporting,
    ] = useState(false);

    const handleToggleLicensed = async (userId: string, licensedNext: boolean) => {
        setPendingUserId(userId);
        try {
            await setMembershipLicensed({ variables: { userId, organizationId, licensed: licensedNext } });
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Could not update the license");
        } finally {
            setPendingUserId(null);
        }
    };

    /*
     * The per-person edit, not a membership one: the flag lives on the user, so this is the same
     * `updateUser` the profile page calls. Apollo writes the returned `ageRestricted` back into the
     * cached row by id, so the switch settles on what the server stored rather than on what was
     * clicked.
     */
    const [updateUser] = useMutation(UpdateUserDocument);
    const [
        pendingAgeUserId,
        setPendingAgeUserId,
    ] = useState<string | null>(null);

    const handleToggleAgeRestricted = async (userId: string, ageRestrictedNext: boolean) => {
        setPendingAgeUserId(userId);
        try {
            await updateUser({ variables: { id: userId, input: { ageRestricted: ageRestrictedNext } } });
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Could not update the age restriction");
        } finally {
            setPendingAgeUserId(null);
        }
    };

    /*
     * The org-wide version. `null` means the dialog is closed; true and false are the two directions,
     * so one piece of state carries both which dialog is open and what it would do.
     */
    const [
        bulkAgeRestrict,
        setBulkAgeRestrict,
    ] = useState<boolean | null>(null);

    /*
     * How many members the action would actually change, counted in the database.
     *
     * Members already at the target value are left alone by the mutation, so this asks for the
     * complement — and it is the number the dialog states. Guessing it from `members` would count
     * the page somebody has scrolled to, which on the largest organization is 50 of 8,635.
     */
    const { data: impactData } = useQuery(OrganizationMembersNotAgeRestrictedDocument, {
        variables: { organizationId, ageRestricted: !bulkAgeRestrict },
        skip: bulkAgeRestrict === null,
        fetchPolicy: "network-only",
    });
    const impactCount = impactData?.organizationMemberCount;

    const [
        setOrgMembersAgeRestricted,
        { loading: bulkRunning },
    ] = useMutation(SetOrganizationMembersAgeRestrictedDocument);

    const handleBulkAgeRestrict = async () => {
        if (bulkAgeRestrict === null) return;
        const target = bulkAgeRestrict;
        try {
            const { data: result } = await setOrgMembersAgeRestricted({
                variables: { organizationId, ageRestricted: target },
                // Every roster row's `ageRestricted` may have moved, and the mutation returns a
                // count rather than the rows — so the list is refetched instead of patched.
                refetchQueries: [
                    OrganizationMembersDocument,
                ],
            });
            const changed = result?.setOrganizationMembersAgeRestricted ?? 0;
            toast.success(
                target
                    ? `Age restricted ${changed} ${changed === 1 ? "member" : "members"}.`
                    : `Lifted the age restriction for ${changed} ${changed === 1 ? "member" : "members"}.`,
            );
            setBulkAgeRestrict(null);
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Could not update the age restriction");
        }
    };

    /*
     * Reads the whole matching roster, a page at a time, rather than exporting `members`.
     *
     * `members` is the window somebody has scrolled to, so exporting it would quietly produce a
     * file of the first fifty members and look like a complete one. An export is a deliberate
     * click and can afford the round trips a page load cannot.
     */
    const handleExportCsv = async () => {
        setExporting(true);
        try {
            const all: Member[] = [];
            for (let offset = 0; offset < EXPORT_MAX_ROWS; offset += EXPORT_PAGE_SIZE) {
                const { data: page } = await client.query<OrganizationMembersQuery>({
                    query: OrganizationMembersDocument,
                    variables: { ...filter, limit: EXPORT_PAGE_SIZE, offset },
                    // Never from the cache: the cached list is the window this tab is showing, and
                    // reading it back would export the same partial set by a longer route.
                    fetchPolicy: "no-cache",
                });
                const rowsPage = page?.organizationMembers ?? [];
                all.push(...rowsPage);
                if (rowsPage.length < EXPORT_PAGE_SIZE) break;
            }
            /*
             * Invitations are paged for the same reason members are.
             *
             * This appended `pending`, which is one page of a capped query — so an export from an
             * organization past the cap walked every member correctly and then attached the first
             * 1,000 of 5,094 invitations, in a file that looks complete. Same loop, same reason.
             */
            const allPending: PendingMember[] = [];
            for (let offset = 0; offset < EXPORT_MAX_ROWS; offset += EXPORT_PAGE_SIZE) {
                const { data: page } = await client.query<OrganizationRosterQuery>({
                    query: OrganizationRosterDocument,
                    variables: { organizationId, status: "PENDING", limit: EXPORT_PAGE_SIZE, offset },
                    fetchPolicy: "no-cache",
                });
                const rowsPage = page?.organizationRoster ?? [];
                allPending.push(...rowsPage);
                if (rowsPage.length < EXPORT_PAGE_SIZE) break;
            }

            exportCsv("org-members", CSV_HEADERS, [
                ...all.map(memberToCsvRow),
                ...allPending.map(pendingToCsvRow),
            ]);
        } finally {
            setExporting(false);
        }
    };

    const addMemberDialog = (
        <AddOrgMemberDialog
            open={addMemberOpen}
            onOpenChange={onAddMemberOpenChange}
            organizationId={organizationId}
            restrictToOrganizationId={restrictToOrganizationId}
            onAdded={onMembersAdded}
        />
    );

    // Keyed on the counted total, not on `members.length`: the first page is still in flight when
    // this renders, and an organization with members would otherwise flash the empty state.
    if (memberCount === 0 && pendingCount === 0) {
        return (
            <>
                <div className="flex flex-col items-center justify-center gap-3 py-32">
                    <div className="bg-muted flex h-12 w-12 items-center justify-center rounded-xl">
                        <Users className="text-muted-foreground h-6 w-6" />
                    </div>
                    <Text weight="medium">No members in this organization</Text>
                    <Text muted className="text-sm">
                        Add members to this organization.
                    </Text>
                </div>
                {addMemberDialog}
            </>
        );
    }

    return (
        <>
            <FilterBar
                className="mb-3"
                search={search}
                onSearchChange={setSearch}
                searchLabel="Search members"
                searchPlaceholder="Search by name or email"
                searchClassName="max-w-xs"
                active={filtersActive}
                onClear={() => {
                    setSearch("");
                    setLicenseFilter(ALL);
                }}
                // Both numbers come from the server. `shown` was `visibleMembers.length`, which is
                // now the size of the loaded window rather than the size of the match.
                count={{
                    shown: (matchingCount ?? members.length) + matchingInvitations,
                    total: memberCount + pendingCount,
                }}
            >
                <Select value={licenseFilter} onValueChange={setLicenseFilter}>
                    <SelectTrigger className="w-40" aria-label="Filter by license">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={ALL}>All members</SelectItem>
                        <SelectItem value="LICENSED">Licensed</SelectItem>
                        <SelectItem value="UNLICENSED">Unlicensed</SelectItem>
                    </SelectContent>
                </Select>
                {/* `modal={false}`: this menu's items open a dialog, and two focus traps handing
                    focus back and forth is a hang rather than a glitch. The menu does not need to
                    trap focus to work. */}
                <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="ml-auto flex w-auto items-center gap-1.5">
                            <ShieldOff className="h-3.5 w-3.5" />
                            Age restriction
                            <ChevronDown className="h-3.5 w-3.5" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => setBulkAgeRestrict(true)}>
                            Restrict all members
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => setBulkAgeRestrict(false)}>
                            Unrestrict all members
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
                <Button
                    variant="outline"
                    size="sm"
                    className="flex w-auto items-center gap-1.5"
                    onClick={() => void handleExportCsv()}
                    disabled={exporting}
                >
                    <Download className="h-3.5 w-3.5" />
                    {exporting ? "Exporting…" : "Export CSV"}
                </Button>
            </FilterBar>

            <ListViewControls view={view} noun="members" />

            <UserTable
                columns={[
                    userColumns.identity,
                    userColumns.phone,
                    userColumns.licensed(
                        (user, licensedNext) => void handleToggleLicensed(user.id, licensedNext),
                        pendingUserId,
                    ),
                    userColumns.ageRestricted(
                        (user, ageRestrictedNext) => void handleToggleAgeRestricted(user.id, ageRestrictedNext),
                        pendingAgeUserId,
                    ),
                    userColumns.status,
                    userColumns.appDownloaded,
                    userColumns.lastActive,
                    userColumns.joined,
                ]}
                rows={rows}
                rowKey={(user) => user.id}
                filtered={filtersActive}
                loading={loading}
                // The largest roster is 8,635 rows. Windowing bounds what is fetched; this bounds
                // what is in the DOM once somebody has scrolled a long way through it — and it is
                // what makes the scrollbar track the rows that are loaded rather than the whole
                // organization.
                virtualize
                noMatches="No members match this search."
            />

            {/* Crossing into the viewport asks for the next page, so it arrives before the
                bottom of the list does. Only rendered while there is more to ask for. */}
            {view.mode === "scroll" && hasMore && <div ref={sentinelRef} aria-hidden className="h-px" />}

            {(loadingMore || loadingMoreInvitations) && (
                <div className="text-muted-foreground py-4 text-center text-xs">Loading more…</div>
            )}

            {/* The blast radius before the action, not after: the count is the members this would
                actually change, and the button repeats the verb (`.claude/rules/ui.md`). */}
            <ConfirmDialog
                open={bulkAgeRestrict !== null}
                onOpenChange={(open) => !open && setBulkAgeRestrict(null)}
                title={bulkAgeRestrict ? "Age restrict all members?" : "Lift the age restriction?"}
                description={
                    bulkAgeRestrict
                        ? `Blocks ${impactCount ?? "…"} ${impactCount === 1 ? "member" : "members"} of this organization from age-restricted content. Members already restricted are unaffected, and anyone who joins later is not restricted by this.`
                        : `Gives ${impactCount ?? "…"} ${impactCount === 1 ? "member" : "members"} of this organization access to age-restricted content. Members who are not restricted are unaffected.`
                }
                confirmLabel={
                    bulkAgeRestrict
                        ? `Restrict ${impactCount ?? 0} ${impactCount === 1 ? "member" : "members"}`
                        : `Unrestrict ${impactCount ?? 0} ${impactCount === 1 ? "member" : "members"}`
                }
                // Not confirmable until the count is on screen — a blast radius nobody has seen yet
                // is the thing the dialog exists to prevent, and the label would read "0 members"
                // while the action restricted the whole roster.
                loading={bulkRunning || impactCount === undefined}
                loadingLabel={impactCount === undefined ? "Counting members…" : undefined}
                onConfirm={() => void handleBulkAgeRestrict()}
            />

            {addMemberDialog}
        </>
    );
}
